import { parseMoney, resolveBookingAmounts } from './bookingAmounts';

/** Stored in bookings.requirements when a booking is saved as free. */
export const LAST_PRICED_SNAPSHOT_KEY = 'last_priced_snapshot';

const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const clonePackages = (packages) =>
  (packages || []).map((line) => ({
    package_id: line.package_id ?? line.id ?? line.item_id ?? null,
    name: line.name ?? line.package_name ?? line.title ?? 'Add-on',
    quantity: line.quantity ?? line.qty ?? 1,
    unit_price: roundMoney(line.unit_price ?? line.unitPrice ?? 0),
    total: roundMoney(line.total ?? line.amount ?? 0),
    amount: roundMoney(line.total ?? line.amount ?? 0)
  }));

export const lastPricedSnapshotHasCharge = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return false;
  const base = parseMoney(snapshot.booking_amount ?? snapshot.base_amount) ?? 0;
  const addOns = parseMoney(snapshot.add_ons_total) ?? 0;
  const pending = parseMoney(snapshot.amount_pending) ?? 0;
  const paid = parseMoney(snapshot.amount_paid) ?? 0;
  if (base > 0 || addOns > 0) return true;
  if (pending > 0 || paid > 0) return true;
  const pkgs = snapshot.packages;
  if (Array.isArray(pkgs)) {
    return pkgs.some((line) => (parseMoney(line?.total ?? line?.amount) ?? 0) > 0);
  }
  return false;
};

export const parseLastPricedSnapshot = (requirements) => {
  if (!requirements || typeof requirements !== 'object') return null;
  const raw = requirements[LAST_PRICED_SNAPSHOT_KEY];
  return raw && typeof raw === 'object' ? raw : null;
};

/** In-drawer ref shape used by the free-booking toggle. */
export const draftPricingFromLastPricedSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const packages = clonePackages(snapshot.packages).map((line, idx) => ({
    _key: `${line.package_id ?? 'row'}-${idx}`,
    ...line
  }));
  const toInput = (value) => {
    const parsed = parseMoney(value);
    return parsed == null ? '0' : String(parsed);
  };
  return {
    bookingAmount: toInput(snapshot.booking_amount),
    addOnsAmount: toInput(snapshot.add_ons_total),
    amountPaid: toInput(snapshot.amount_paid),
    amountPending: toInput(snapshot.amount_pending),
    draftPackages: packages
  };
};

export const lastPricedSnapshotFromDraftPricing = ({
  bookingAmount,
  addOnsAmount,
  amountPaid,
  amountPending,
  draftPackages
}) => ({
  booking_amount: roundMoney(parseMoney(bookingAmount) ?? 0),
  add_ons_total: roundMoney(parseMoney(addOnsAmount) ?? 0),
  amount_paid: roundMoney(parseMoney(amountPaid) ?? 0),
  amount_pending: roundMoney(parseMoney(amountPending) ?? 0),
  packages: clonePackages(draftPackages),
  saved_at: new Date().toISOString()
});

export const lastPricedSnapshotFromBooking = (booking) => {
  if (!booking || typeof booking !== 'object') return null;
  const amounts = resolveBookingAmounts(booking);
  if (amounts.grandTotal <= 0 && amounts.amountPending <= 0 && amounts.baseAmount <= 0) {
    return null;
  }
  const req = booking.requirements;
  const packages =
    (req && typeof req === 'object' && req.packages) || booking.packages || [];
  return lastPricedSnapshotFromDraftPricing({
    bookingAmount: amounts.baseAmount,
    addOnsAmount: amounts.addOnsTotal,
    amountPaid: amounts.amountPaid,
    amountPending: amounts.amountPending,
    draftPackages: clonePackages(packages).map((line, idx) => ({
      _key: `${line.package_id ?? 'row'}-${idx}`,
      ...line
    }))
  });
};

/** Pick snapshot to persist when saving as free (ref → existing → booking row). */
export const resolveLastPricedSnapshotForFreeSave = ({
  requirements,
  inMemoryRef,
  booking
}) => {
  if (inMemoryRef) {
    const fromRef = lastPricedSnapshotFromDraftPricing({
      bookingAmount: inMemoryRef.bookingAmount,
      addOnsAmount: inMemoryRef.addOnsAmount,
      amountPaid: inMemoryRef.amountPaid,
      amountPending: inMemoryRef.amountPending,
      draftPackages: inMemoryRef.draftPackages
    });
    if (lastPricedSnapshotHasCharge(fromRef)) return fromRef;
  }

  const existing = parseLastPricedSnapshot(requirements);
  if (lastPricedSnapshotHasCharge(existing)) return existing;

  return lastPricedSnapshotFromBooking(booking);
};

/** Initialize drawer ref from requirements snapshot or live booking amounts. */
export const resolveInitialPricingRef = ({ requirements, booking, packages, amounts }) => {
  const hasActiveCharge =
    (amounts?.grandTotal ?? 0) > 0 ||
    (amounts?.amountPending ?? 0) > 0 ||
    (amounts?.baseAmount ?? 0) > 0;

  if (hasActiveCharge) {
    const toInput = (value) => {
      const parsed = parseMoney(value);
      return parsed == null ? '0' : String(parsed);
    };
    return {
      bookingAmount: toInput(amounts.baseAmount),
      addOnsAmount: toInput(amounts.addOnsTotal),
      amountPaid: toInput(amounts.amountPaid),
      amountPending: toInput(amounts.amountPending),
      draftPackages: (packages || []).map((line) => ({ ...line }))
    };
  }

  const fromStored = draftPricingFromLastPricedSnapshot(parseLastPricedSnapshot(requirements));
  if (fromStored && lastPricedSnapshotHasCharge(parseLastPricedSnapshot(requirements))) {
    return fromStored;
  }

  const fromBooking = lastPricedSnapshotFromBooking(booking);
  if (fromBooking && lastPricedSnapshotHasCharge(fromBooking)) {
    return draftPricingFromLastPricedSnapshot(fromBooking);
  }

  const toInput = (value) => {
    const parsed = parseMoney(value);
    return parsed == null ? '0' : String(parsed);
  };
  return {
    bookingAmount: toInput(amounts.baseAmount),
    addOnsAmount: toInput(amounts.addOnsTotal),
    amountPaid: toInput(amounts.amountPaid),
    amountPending: toInput(amounts.amountPending),
    draftPackages: (packages || []).map((line) => ({ ...line }))
  };
};
